import React, { useState } from 'react';
import { Modal, Form, Input, Radio, Button, message, Space, Typography, Alert, Tooltip } from 'antd';
import { MailOutlined, PhoneOutlined, CopyOutlined, CheckCircleOutlined, MessageOutlined } from '@ant-design/icons';
import { estimateApi } from '../../services/api';
import { copyToClipboard } from '../../utils/clipboard';
import { useMissingRequirements } from '../../hooks/useMissingRequirements';
import MissingRequirementsChecklist from './MissingRequirementsChecklist';
import { Customer, Vehicle } from '../../types';

const { Text } = Typography;

interface SendEstimateModalProps {
  visible: boolean;
  onClose: () => void;
  estimateId: number;
  estimateNumber: string;
  customerEmail?: string;
  customerPhone?: string;
  /**
   * G2 — the Send-dialog checkpoint. Passed so this dialog can show what the
   * customer will be asked for at approval, and let staff waive a field that does
   * not apply. All optional: without them the checkpoint simply does not render
   * and sending behaves exactly as it did before.
   */
  customerId?: number | null;
  customer?: Customer | null;
  vehicle?: Vehicle | null;
  onSent?: () => void;
}

const SendEstimateModal: React.FC<SendEstimateModalProps> = ({
  visible,
  onClose,
  estimateId,
  estimateNumber,
  customerEmail,
  customerPhone,
  customerId,
  customer,
  vehicle,
  onSent,
}) => {
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [publicUrl, setPublicUrl] = useState<string>('');
  /** Fields waived from inside this dialog — dropped from the list immediately. */
  const [waived, setWaived] = useState<string[]>([]);

  /**
   * What the customer will be stopped for at approval.
   *
   * Scoped to this estimate and customer so an exception already granted for
   * either is honoured. Asked only once the dialog is open, and computed by the
   * SAME hook the estimate screen's SendDialog uses — one diff, not two.
   */
  const { missing } = useMissingRequirements({
    customerId: customerId ?? null,
    estimateId,
    customer,
    vehicle,
    waived,
    enabled: visible && !sent,
  });

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      await handleSend(values);
    } catch (error) {
      console.error('Form validation error:', error);
    }
  };

  const handleSend = async (values: any) => {
    try {
      setSending(true);

      const response = await estimateApi.send(estimateId, {
        method: 'email',
        to: values.to,
        shopName: values.shopName || 'CNF Auto LLC',
        shopPhone: values.shopPhone,
      });

      setPublicUrl(response.data.publicUrl);
      setSent(true);
      message.success('Estimate sent successfully via email!');

      if (onSent) {
        onSent();
      }
    } catch (error: any) {
      console.error('Error sending estimate:', error);
      if (error.response?.status === 501) {
        message.error('SMS sending is not currently available. Please send via email.', 5);
      } else if (error.response?.data?.error) {
        message.error(error.response.data.error, 5);
      } else if (error.response?.data?.detail) {
        message.error(error.response.data.detail, 5);
      } else {
        message.error('Failed to send estimate. Please try again.', 5);
      }
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setWaived([]);
    setSent(false);
    setPublicUrl('');
    onClose();
  };

  const handleCopyLink = async () => {
    const success = await copyToClipboard(publicUrl);
    if (success) {
      message.success('Link copied to clipboard!');
    } else {
      message.error('Failed to copy link');
    }
  };

  return (
    <Modal
      title={
        <Space>
          {sent ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} /> : <MailOutlined />}
          <span>{sent ? 'Estimate Sent!' : `Send Estimate #${estimateNumber}`}</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={
        sent ? [
          <Button key="copy" icon={<CopyOutlined />} onClick={handleCopyLink}>
            Copy Link
          </Button>,
          <Button key="done" type="primary" onClick={handleClose}>
            Done
          </Button>,
        ] : [
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            loading={sending}
            onClick={handleFormSubmit}
            icon={<MailOutlined />}
          >
            Send Email
          </Button>,
        ]
      }
      width={600}
      centered
    >
      {sent ? (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            message="Estimate sent successfully!"
            description={`The customer will receive a link to view and approve the estimate.`}
            type="success"
            showIcon
          />

          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Public Link:
            </Text>
            <Input
              value={publicUrl}
              readOnly
              addonAfter={
                <CopyOutlined
                  style={{ cursor: 'pointer' }}
                  onClick={handleCopyLink}
                />
              }
            />
            <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
              You can also share this link manually with the customer
            </Text>
          </div>
        </Space>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          initialValues={{
            method: 'email',
            to: customerEmail,
            shopName: 'CNF Auto LLC',
          }}
        >
          <Form.Item
            label="Send Method"
            name="method"
            rules={[{ required: true, message: 'Please select a send method' }]}
          >
            <Radio.Group>
              <Radio.Button value="email">
                <MailOutlined /> Email
              </Radio.Button>
              <Tooltip title="SMS sending is not yet configured. Use email to send the estimate.">
                <Radio.Button value="sms" disabled>
                  <PhoneOutlined /> SMS
                </Radio.Button>
              </Tooltip>
              <Tooltip title="SMS sending is not yet configured. Use email to send the estimate.">
                <Radio.Button value="both" disabled>
                  <MessageOutlined /> Both
                </Radio.Button>
              </Tooltip>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="Recipient Email"
            name="to"
            rules={[
              { required: true, message: "Please enter the recipient's email" },
              { type: 'email', message: 'Please enter a valid email address' },
            ]}
          >
            <Input
              placeholder="customer@example.com"
              prefix={<MailOutlined />}
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Shop Name"
            name="shopName"
            tooltip="This will appear in the email"
          >
            <Input placeholder="CNF Auto LLC" size="large" />
          </Form.Item>

          <Form.Item
            label="Shop Phone (Optional)"
            name="shopPhone"
            tooltip="Will be included for customer contact"
          >
            <Input placeholder="(555) 123-4567" size="large" />
          </Form.Item>

          <Alert
            message="What happens next?"
            description="The customer will receive an email with a link to view and approve the estimate online. They can sign digitally on any device."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />

          {/* G2. Sits directly above the Send button and never disables it
              (spec D3): staff are informed, never blocked. */}
          <MissingRequirementsChecklist
            estimateId={estimateId}
            customerId={customerId ?? customer?.id ?? null}
            missing={missing}
            onWaived={(field) => setWaived((prev) => [...prev, field])}
          />
        </Form>
      )}
    </Modal>
  );
};

export default SendEstimateModal;
